import type { RouteLocationNormalized } from 'vue-router';

/**
 * --------------------------------------------------------------------
 */
export default function usePromotions(route?: RouteLocationNormalized) {
    const { t: $t } = useNuxtApp().$i18n;
    const { product } = useProduct(route);
    const { setErrorToast } = useToasts();

    /**
     * Simple sanitation so we don't send too much garbage to the backend
     */
    function sanitizePromotionCode(promotion: string): string | undefined {
        const sanitized = promotion.replace(/[^\w-]/g, '').slice(0, 50);
        return sanitized || undefined;
    }

    /**
     * Validate if the promotion is valid and applicable to the current product
     */
    function validatePromotionData(data: PromotionData, product: Product): void {
        if (!data?.validity.redeemable) {
            throw new Error('promotion-non-redeemable');
        }

        if (!data?.validity.active) {
            throw new Error('promotion-non-active');
        }

        // If the promotion restrictions doesn't match the product type, throw error
        // (Check if promotion has activity or p2p restrictions and if product is of that type)
        const hasActivityMismatch = product.config.type !== 'Activity' && data?.restrictions.validActivityIds.length > 0;
        const hasP2PMismatch = product.config.type !== 'P2P' && (data?.restrictions.validOrigins.length || data?.restrictions.validDestinations.length) > 0;

        if (hasActivityMismatch || hasP2PMismatch) {
            throw new Error('promotion-type-mismatch');
        }

        // Check activity restrictions for activity products
        if (product.config.type === 'Activity') {
            const productActivity = product as ProductActivity;
            if (data?.restrictions.validActivityIds.length > 0 && data?.restrictions.validActivityIds.includes(productActivity.activityId) === false) {
                throw new Error('promotion-type-mismatch');
            }
        }
        // Check P2P restrictions for P2P products
        else if (product.config.type === 'P2P') {
            const productP2P = product as ProductP2P;

            const promoDestinations = data.restrictions.validDestinations.map(dest => dest.ref);
            const productDestinations = productP2P.config.destinations?.map(dest => dest.ref) || [];
            const hasMatchingDestination = promoDestinations.length === 0 || productDestinations.some(productRef => promoDestinations.includes(productRef));

            // Promotion is not valid for any of the product's destinations
            if (!hasMatchingDestination) {
                throw new Error('promotion-destination-mismatch');
            }
        }
    }

    /**
     * Maps promotion error codes to i18n keys
     */
    function getPromotionErrorMessage(errorMessage: string): string {
        const errorToI18nKey: Record<string, string> = {
            'promotion-non-redeemable': 'offerFlow.promotion.validation.notRedeemable',
            'promotion-non-active': 'offerFlow.promotion.validation.notActive',
            'promotion-type-mismatch': 'offerFlow.promotion.validation.typeMismatch',
            'promotion-destination-mismatch': 'offerFlow.promotion.validation.destinationMismatch',
            'promotion-not-loaded': 'offerFlow.promotion.validation.genericError',
            'promotion-required': 'offerFlow.promotion.validation.required',
        };

        return $t(errorToI18nKey[errorMessage] || 'promotion.dialog.genericError');
    }

    /**
     * Apply promotion on product and add restrictions if it's a P2P
     */
    async function applyPromotion(promotionCode: string | undefined): Promise<boolean> {
        if (!promotionCode) {
            if (product.value.config.isPromotionRequired) {
                throw new Error('promotion-required');
            }
            return false;
        }

        const response = await apiLoadPromotion(promotionCode);
        const options = response.data;

        if (options) {
            validatePromotionData(options, product.value);
            product.value.promotion = options.promotion;

            if (product.value.config.type === 'P2P') {
                (product.value as ProductP2P).restrictions = {
                    validOrigins: options.restrictions.validOrigins,
                    validDestinations: options.restrictions.validDestinations,
                    validRouteVariants: options.restrictions.validNamedRoutes as RouteVariant[],
                    enforceRoundtrip: options.restrictions.enforceRoundtrip,
                };
            }
            return true;
        }
        throw new Error('promotion-not-loaded');
    }

    /**
     * Apply promotion with toast error handling (for middleware usage)
     */
    async function applyPromotionWithToasts(promotionCode: string | undefined): Promise<void> {
        const isRequired = product.value.config.isPromotionRequired;

        if (!promotionCode && !isRequired) {
            return;
        }

        try {
            const sanitizedPromoCode = promotionCode ? sanitizePromotionCode(promotionCode) : undefined;
            await applyPromotion(sanitizedPromoCode);
        }
        catch (error) {
            const message = getPromotionErrorMessage((error as Error).message);
            setErrorToast(message);

            if (isRequired) {
                throw createError({
                    statusCode: 403,
                    message,
                    fatal: true,
                    cause: error,
                });
            }
        }
    }

    return {
        applyPromotion,
        applyPromotionWithToasts,
        getPromotionErrorMessage,
    };
}
